/**
 * PV Quiz Viking - Quiz Engine
 * Primal Viking - 9 Question Quiz Funnel
 *
 * Fully independent from croev-quiz-v1.js (no shared code) so the other
 * live quiz funnels built on that engine cannot be affected by this file.
 *
 * Navigation model: the URL hash (#step-N) is the single source of truth
 * for the current step. The on-screen back arrow and the browser Back
 * button both resolve to the same code path (history.back() -> hashchange).
 */

(function () {
  'use strict';

  var ANSWERS_KEY = 'pv_quiz_viking_answers';
  var META_KEY = 'pv_quiz_viking_meta';
  // Lead record (answers + name/email) kept in localStorage - unlike the
  // sessionStorage keys above, it must outlive the tab for later visits.
  var QUIZ_RECORD_KEY = 'pv_quiz';

  // Email Step lives on hash #step-18 so the existing 13-17 numbering (and
  // every link/rule pointing at #step-14) stays untouched. FLOW_ORDER is
  // what actually places it between Loading (12) and Confirmation (13).
  var GATE_STEP = 18;
  var FLOW_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, GATE_STEP, 13, 14, 15, 16, 17];

  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  // Human-readable names for each question's answer, used as the lead
  // record keys and as cart attributes (_quiz_<name>) so they show on the order.
  var QUESTION_ATTRIBUTE_NAMES = {
    q1: 'goal',
    q2: 'blockers',
    q3: 'age',
    q4: 'exercise',
    q5: 'energy_dip',
    q6: 'morning',
    q7: 'body_type',
    q8: 'tried',
    q9: 'readiness'
  };

  // Screen 13 (Offer Page) is composed of this container's own steps PLUS
  // several reused, untouched standalone sections named with this prefix in
  // the JSON template. Toggling them lives here, in one place, alongside the
  // container's own step toggle. Shopify prefixes JSON-template section ids
  // with "template--<id>__" before the section key, so match "__q13_"
  // anywhere in the id, not a strict "starts with" prefix.
  var Q13_SECTION_SELECTOR = '[id*="__q13_"]';

  // Steps here are skipped unless their predicate returns true for the current
  // answers/meta. Hash numbering: Intro=1, Q1-Q9=2-10, Results=11, Loading=12,
  // Confirmation=13, Offer=14, Order Summary=15, Upsell=16 (no step-0 - Intro
  // is step 1).
  var STEP_SKIP_RULES = {
    16: function (answers, meta) {
      // Already have the add-on (accepted early on the Offer Page's
      // always-shown card) - no point pitching it again.
      if (meta && (meta.addonAccepted || meta.addonInCartVariantId)) return false;
      return Array.isArray(answers.q2) && answers.q2.indexOf('Low Drive / Libido') !== -1;
    }
  };

  var STEP_NAMES = {
    1: 'intro',
    2: 'q1',
    3: 'q2',
    4: 'q3',
    5: 'q4',
    6: 'q5',
    7: 'q6',
    8: 'q7',
    9: 'q8',
    10: 'q9',
    11: 'results',
    12: 'loading',
    13: 'confirmation',
    14: 'offer',
    15: 'order_summary',
    16: 'upsell',
    17: 'checkout_redirect',
    18: 'email_gate'
  };

  function readJSON(key, fallback) {
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* sessionStorage unavailable (private mode, etc.) - quiz still works, just won't persist */
    }
  }

  // Parses a JSON string handed over via a data-* attribute (e.g. the
  // upsell button's list of selling plans). Unlike readJSON, this isn't
  // keyed off sessionStorage - just a plain, failure-safe JSON.parse.
  function parseJSON(str, fallback) {
    try {
      return str ? JSON.parse(str) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  // Selling plan names here are always "Deliver every N days" (see
  // pv-quiz-viking-upsell.liquid / pv-quiz-viking-offer-header.liquid) -
  // pull the day count back out so it can be compared against the main
  // product's own refill-days figure.
  function parseDaysFromPlanName(name) {
    var match = /(\d+)/.exec(name || '');
    return match ? parseInt(match[1], 10) : null;
  }

  // Parses a shop money-formatted string (e.g. "$49.99", "$1,234.56") into a
  // plain number. Assumes '.' as the decimal separator and ',' as a
  // thousands separator, which covers this store's currency formatting.
  function parseMoneyValue(str) {
    if (!str) return null;
    var cleaned = String(str).replace(/[^0-9.]/g, '');
    var value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
  }

  // Reuses the currency symbol/placement of an existing money-formatted
  // string (the source of truth for this shop's formatting) and swaps in a
  // new numeric value, rather than hardcoding a currency symbol here.
  function formatMoneyLike(template, value) {
    var match = String(template).match(/^(\D*)[\d,.]+(\D*)$/);
    if (!match) return template;
    return match[1] + value.toFixed(2) + match[2];
  }

  function formatAnswerValue(value) {
    var text = String(value).replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function readQuizRecord() {
    try {
      var raw = localStorage.getItem(QUIZ_RECORD_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeQuizRecord(record) {
    try {
      localStorage.setItem(QUIZ_RECORD_KEY, JSON.stringify(record));
    } catch (e) {
      /* localStorage unavailable - the quiz still continues to results */
    }
  }

  // { goal: 'Reduce Belly', blockers: ['Low Energy', ...], ... } - answer
  // text as shown on the options, never option indexes.
  function buildReadableAnswers(answers) {
    var readable = {};
    Object.keys(answers).forEach(function (key) {
      var value = answers[key];
      if (value == null || value === '' || (Array.isArray(value) && !value.length)) return;
      var name = QUESTION_ATTRIBUTE_NAMES[key] || key;
      readable[name] = Array.isArray(value) ? value.map(formatAnswerValue) : formatAnswerValue(value);
    });
    return readable;
  }

  var PVQuizViking = /** @class */ (function () {
    function PVQuizViking(container) {
      this.container = container;
      this.steps = Array.prototype.slice.call(container.querySelectorAll('[data-step]'));
      this.answers = readJSON(ANSWERS_KEY, {});
      this.meta = readJSON(META_KEY, { skippedIntro: false, selectedTier: null, purchaseType: 'subscription' });
      this.timers = [];

      this.quizTemplate = container.dataset.quizTemplate || 'pv-quiz-viking';
      // Name/email from an earlier run, kept for prefill once a new run
      // overwrites pv_quiz.
      var previousRecord = readQuizRecord();
      this.previousLead = previousRecord
        ? { first_name: previousRecord.first_name, email: previousRecord.email }
        : null;
      // Section setting "Enable email step" - the snippet is only rendered
      // when it's on, so also require the step to actually be in the DOM.
      this.emailStepEnabled =
        container.dataset.emailStep === 'true' && !!container.querySelector('[data-step="' + GATE_STEP + '"]');

      this.bindEvents();

      this.skipIntro = container.dataset.skipIntro === 'true';
      // Results (11) and confirmation (13) screens are bypassed when enabled:
      // Q-last -> loading (12) -> offer/PDP (14).
      this.skippedSteps = container.dataset.skipResults === 'true' ? [11, 13] : [];

      if (!window.location.hash || (this.skipIntro && this.readStepFromHash() === 1)) {
        history.replaceState(null, '', this.skipIntro ? '#step-2' : '#step-1');
      }
      this.render(this.readStepFromHash());

      var self = this;
      window.addEventListener('hashchange', function () {
        self.render(self.readStepFromHash());
      });
    }

    PVQuizViking.prototype.readStepFromHash = function () {
      var match = /^#step-(\d+)$/.exec(window.location.hash);
      var step = match ? parseInt(match[1], 10) : 1;
      // Stale/shared #step-18 link while the Email Step is switched off.
      if (step === GATE_STEP && !this.emailStepEnabled) return this.getNextStep(GATE_STEP);
      return this.skipIntro && step === 1 ? 2 : step;
    };

    PVQuizViking.prototype.setMeta = function (partial) {
      Object.assign(this.meta, partial);
      writeJSON(META_KEY, this.meta);
    };

    PVQuizViking.prototype.trackEvent = function (name, payload) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, payload));
    };

    PVQuizViking.prototype.goToStep = function (n) {
      if (window.location.hash === '#step-' + n) {
        this.render(n);
      } else {
        window.location.hash = '#step-' + n;
      }
    };

    PVQuizViking.prototype.bindEvents = function () {
      var self = this;

      this.container.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-gate-form]');
        if (!form) return;
        e.preventDefault();
        self.handleGateSubmit(form);
      });

      this.container.addEventListener('input', function (e) {
        // Clear a field's error as soon as the shopper starts fixing it.
        if (e.target.closest('[data-gate-form]')) self.setGateFieldError(e.target, false);
      });

      this.container.addEventListener('click', function (e) {
        var option = e.target.closest('.pv-quiz-viking__option');
        if (option) {
          self.handleOptionSelect(option);
          return;
        }

        var backBtn = e.target.closest('[data-action="back"]');
        if (backBtn) {
          history.back();
          return;
        }

        var startBtn = e.target.closest('[data-action="start"]');
        if (startBtn) {
          self.startQuizRecord();
          self.goToStep(2);
          return;
        }

        var skipBtn = e.target.closest('[data-action="skip-to-offer"]');
        if (skipBtn) {
          var fromStep = parseInt(skipBtn.closest('[data-step]').dataset.step, 10);
          self.meta.skippedIntro = true;
          writeJSON(META_KEY, self.meta);

          // Skipping ahead always lands on the fully generic Offer Page, as if
          // no questions had been answered at all - clear every question
          // answer (not just from this step onward), regardless of how much
          // was actually filled in this run.
          for (var s = 2; s <= 10; s++) {
            if (STEP_NAMES[s]) delete self.answers[STEP_NAMES[s]];
          }
          writeJSON(ANSWERS_KEY, self.answers);
          self.syncQuizRecordAnswers();

          self.trackEvent(fromStep === 1 ? 'quiz_intro_skip' : 'quiz_question_skip', {
            destination_step: 14,
            from_step: fromStep
          });
          self.goToStep(14);
          return;
        }

        var continueBtn = e.target.closest('[data-action="continue"]');
        if (continueBtn && !continueBtn.disabled) {
          var step = continueBtn.closest('[data-step]');
          self.goToStep(self.getNextStep(parseInt(step.dataset.step, 10)));
          return;
        }

        var gateBackBtn = e.target.closest('[data-action="gate-back"]');
        if (gateBackBtn) {
          // history.back() would land on Loading, which just auto-advances
          // straight back here - return to the last question instead (its
          // answer is restored on render).
          self.goToStep(self.getLastQuestionStep());
          return;
        }

        var gateSkipBtn = e.target.closest('[data-action="gate-skip"]');
        if (gateSkipBtn) {
          self.handleGateSkip();
          return;
        }

        var backToOfferBtn = e.target.closest('[data-action="back-to-offer"]');
        if (backToOfferBtn) {
          self.goToStep(14);
          return;
        }

        var checkoutBtn = e.target.closest('[data-action="checkout-or-upsell"]');
        if (checkoutBtn) {
          var showUpsell = STEP_SKIP_RULES[16] && STEP_SKIP_RULES[16](self.answers, self.meta);
          if (showUpsell) {
            self.goToStep(16);
          } else {
            self.completeCheckout();
          }
          return;
        }

        var upsellAcceptBtn = e.target.closest('[data-action="upsell-accept"]');
        if (upsellAcceptBtn) {
          self.trackEvent('quiz_upsell_accepted', { product: 'libido_fire_drops' });
          self.setMeta({
            addonAccepted: true,
            addonVariantId: upsellAcceptBtn.dataset.variantId,
            addonSellingPlanId: upsellAcceptBtn.dataset.sellingPlanId || null,
            // Full list of {id, name} plans this variant offers - resolved
            // against the main product's chosen delivery frequency at
            // checkout, not frozen to whichever plan was the default here.
            addonSellingPlans: parseJSON(upsellAcceptBtn.dataset.sellingPlans),
            addonTitle: upsellAcceptBtn.dataset.title,
            addonPrice: upsellAcceptBtn.dataset.price,
            addonComparePrice: upsellAcceptBtn.dataset.comparePrice
          });
          self.completeCheckout();
          return;
        }

        var upsellDeclineBtn = e.target.closest('[data-action="upsell-decline"]');
        if (upsellDeclineBtn) {
          self.trackEvent('quiz_upsell_declined', {});
          // Declining here is the more deliberate/final choice - it wins even
          // if the "recommended add-on" card back on the Offer Page (14) had
          // already set addonAccepted to true.
          self.setMeta({
            addonAccepted: false,
            addonVariantId: null,
            addonSellingPlanId: null,
            addonSellingPlans: null,
            addonTitle: null,
            addonPrice: null,
            addonComparePrice: null
          });
          self.completeCheckout();
          return;
        }

        var restartBtn = e.target.closest('[data-action="restart-quiz"]');
        if (restartBtn) {
          // Lets a shopper who bailed on checkout (browser Back lands them
          // back on Screen 17) or is just waiting on the redirect start over,
          // rather than being stuck looking at a dead-end toast.
          self.answers = {};
          self.meta = { skippedIntro: false, selectedTier: null, purchaseType: 'subscription' };
          writeJSON(ANSWERS_KEY, self.answers);
          writeJSON(META_KEY, self.meta);
          self.startQuizRecord();
          self.trackEvent('quiz_restarted', { from_step: 17 });
          self.goToStep(1);
          return;
        }

      });
    };

    PVQuizViking.prototype.isStepSkipped = function (n) {
      if (n === GATE_STEP && !this.emailStepEnabled) return true;
      return (
        this.skippedSteps.indexOf(n) !== -1 ||
        (STEP_SKIP_RULES[n] && !STEP_SKIP_RULES[n](this.answers, this.meta))
      );
    };

    PVQuizViking.prototype.getNextStep = function (current) {
      var index = FLOW_ORDER.indexOf(current);
      var next = index === -1 ? current + 1 : FLOW_ORDER[index + 1];
      while (next != null && this.isStepSkipped(next)) {
        index = FLOW_ORDER.indexOf(next);
        next = index === -1 ? next + 1 : FLOW_ORDER[index + 1];
      }
      return next != null ? next : current + 1;
    };

    // --- Email Step (gate) ---

    PVQuizViking.prototype.handleEmailGateView = function (step) {
      this.trackEvent('quiz_gate_view', { quiz_template: this.quizTemplate });

      // Returning shopper: prefill whatever they gave us last time.
      var lead = this.getKnownLead();
      var nameInput = step.querySelector('[data-gate-name]');
      var emailInput = step.querySelector('[data-gate-email]');
      if (nameInput && !nameInput.value && lead.first_name) nameInput.value = lead.first_name;
      if (emailInput && !emailInput.value && lead.email) emailInput.value = lead.email;
    };

    // Name/email from this run's record, else from the previous run's record
    // (captured before startQuizRecord() overwrote it).
    PVQuizViking.prototype.getKnownLead = function () {
      var record = readQuizRecord();
      var previous = this.previousLead || {};
      return {
        first_name: (record && record.first_name) || previous.first_name || null,
        email: (record && record.email) || previous.email || null
      };
    };

    // A new quiz run starts: overwrite pv_quiz with a fresh record.
    PVQuizViking.prototype.startQuizRecord = function () {
      writeQuizRecord({
        quiz_template: this.quizTemplate,
        answers: buildReadableAnswers(this.answers),
        first_name: null,
        email: null,
        skipped: false,
        completed_at: null
      });
    };

    // Keep pv_quiz.answers in step with every answer, so they survive a refresh.
    PVQuizViking.prototype.syncQuizRecordAnswers = function () {
      var record = readQuizRecord();
      if (!record || record.quiz_template !== this.quizTemplate) {
        this.startQuizRecord();
        return;
      }
      record.answers = buildReadableAnswers(this.answers);
      writeQuizRecord(record);
    };

    PVQuizViking.prototype.getLastQuestionStep = function () {
      var last = 2;
      this.steps.forEach(function (step) {
        var type = step.dataset.stepType;
        if (type === 'question_single' || type === 'question_multi') {
          last = Math.max(last, parseInt(step.dataset.step, 10));
        }
      });
      return last;
    };

    PVQuizViking.prototype.setGateFieldError = function (input, hasError) {
      if (!input || !input.name) return;
      var form = input.closest('[data-gate-form]');
      var errorEl = form && form.querySelector('[data-gate-error="' + input.name + '"]');
      if (hasError) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
      if (errorEl) errorEl.hidden = !hasError;
    };

    PVQuizViking.prototype.saveQuizRecord = function (fields) {
      var record = Object.assign(
        {
          quiz_template: this.quizTemplate,
          answers: buildReadableAnswers(this.answers),
          first_name: null,
          email: null,
          skipped: false,
          completed_at: new Date().toISOString()
        },
        fields
      );
      writeQuizRecord(record);
      return record;
    };

    PVQuizViking.prototype.handleGateSubmit = function (form) {
      var nameInput = form.querySelector('[data-gate-name]');
      var emailInput = form.querySelector('[data-gate-email]');
      var consentInput = form.querySelector('[data-gate-consent]');

      var firstName = nameInput ? nameInput.value.trim() : '';
      var email = emailInput ? emailInput.value.trim() : '';
      var nameValid = firstName !== '';
      var emailValid = EMAIL_PATTERN.test(email);

      this.setGateFieldError(nameInput, !nameValid);
      this.setGateFieldError(emailInput, !emailValid);

      if (!nameValid || !emailValid) {
        (nameValid ? emailInput : nameInput).focus();
        return;
      }

      var submitBtn = form.querySelector('[data-gate-submit]');
      if (submitBtn && submitBtn.getAttribute('aria-busy') === 'true') return; // double tap

      var consent = !!(consentInput && consentInput.checked);
      var record = this.saveQuizRecord({ first_name: firstName, email: email, skipped: false });
      this.trackEvent('quiz_gate_submit', { quiz_template: this.quizTemplate, consent: consent });

      var self = this;
      function advance() {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.disabled = false;
        }
        self.goToStep(self.getNextStep(GATE_STEP));
      }

      // Klaviyo resolves in <= 2s even on failure - results always show.
      var Klaviyo = window.PVQuizKlaviyo;
      if (!Klaviyo) {
        advance();
        return;
      }
      if (submitBtn) {
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.disabled = true;
      }
      var productTitleEl = document.querySelector('[id*="__q13_product"] .pv-product__title');
      Klaviyo.submit({
        email: email,
        firstName: firstName,
        consent: consent,
        quizTemplate: this.quizTemplate,
        answers: record.answers,
        recommendedProduct: productTitleEl ? productTitleEl.textContent.trim() : ''
      }).then(advance);
    };

    PVQuizViking.prototype.handleGateSkip = function () {
      // Skipping this run shouldn't wipe a name/email given on an earlier one.
      var lead = this.getKnownLead();
      this.saveQuizRecord({ first_name: lead.first_name, email: lead.email, skipped: true });
      this.trackEvent('quiz_gate_skip', { quiz_template: this.quizTemplate });
      this.goToStep(this.getNextStep(GATE_STEP));
    };

    // Cart attributes (_quiz_goal, _quiz_blockers, ...) so the answers land on
    // the order. Every known key is sent, blank when unanswered, so a
    // previous run's answers never linger on the cart.
    PVQuizViking.prototype.buildCartAttributes = function () {
      var readable = buildReadableAnswers(this.answers);
      var attributes = { _quiz_template: this.quizTemplate };
      Object.keys(QUESTION_ATTRIBUTE_NAMES).forEach(function (key) {
        var name = QUESTION_ATTRIBUTE_NAMES[key];
        var value = readable[name];
        attributes['_quiz_' + name] = Array.isArray(value) ? value.join(', ') : value || '';
      });
      return attributes;
    };

    PVQuizViking.prototype.handleOptionSelect = function (option) {
      var step = option.closest('[data-step]');
      var questionId = step.dataset.questionId;
      var selectionType = step.dataset.selectionType;
      var value = option.dataset.optionValue;

      if (selectionType === 'single') {
        step.querySelectorAll('.pv-quiz-viking__option').forEach(function (opt) {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');

        // First answer of a fresh run (e.g. intro skipped) - start a new record.
        if (!Object.keys(this.answers).length) this.startQuizRecord();
        this.answers[questionId] = value;
        writeJSON(ANSWERS_KEY, this.answers);
        this.syncQuizRecordAnswers();
        this.trackEvent('quiz_answer_selected', { question: questionId, value: value });

        var nextStep = this.getNextStep(parseInt(step.dataset.step, 10));
        var self = this;
        setTimeout(function () {
          self.goToStep(nextStep);
        }, 250);
        return;
      }

      // Multi-select
      var maxSelections = parseInt(step.dataset.maxSelections, 10) || 0;
      var clearAllValue = step.dataset.clearAllValue || '';
      var isClearAllOption = clearAllValue !== '' && value === clearAllValue;
      var wasSelected = option.classList.contains('is-selected');

      if (isClearAllOption) {
        step.querySelectorAll('.pv-quiz-viking__option').forEach(function (opt) {
          opt.classList.remove('is-selected');
          opt.setAttribute('aria-pressed', 'false');
        });
        if (!wasSelected) {
          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
        }
      } else if (wasSelected) {
        option.classList.remove('is-selected');
        option.setAttribute('aria-pressed', 'false');
      } else {
        var selectedCount = step.querySelectorAll('.pv-quiz-viking__option.is-selected').length;
        if (maxSelections > 0 && selectedCount >= maxSelections) {
          return; // block a tap past the max
        }
        // Selecting a normal option clears a previously-selected "clear all" option
        if (clearAllValue !== '') {
          var clearAllOption = step.querySelector('.pv-quiz-viking__option[data-option-value="' + clearAllValue + '"]');
          if (clearAllOption) {
            clearAllOption.classList.remove('is-selected');
            clearAllOption.setAttribute('aria-pressed', 'false');
          }
        }
        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
      }

      var selectedOptions = Array.prototype.slice.call(step.querySelectorAll('.pv-quiz-viking__option.is-selected'));
      if (!Object.keys(this.answers).length) this.startQuizRecord();
      this.answers[questionId] = selectedOptions.map(function (opt) {
        return opt.dataset.optionValue;
      });
      writeJSON(ANSWERS_KEY, this.answers);
      this.syncQuizRecordAnswers();
      this.trackEvent('quiz_answer_selected', { question: questionId, value: this.answers[questionId].join(', ') });

      this.updateMultiUI(step, selectedOptions.length, maxSelections);

      // Hitting the cap is as decisive as a single-select tap - move on
      // automatically instead of making them find and press Continue.
      if (maxSelections > 0 && selectedOptions.length === maxSelections) {
        var nextStep = this.getNextStep(parseInt(step.dataset.step, 10));
        var self = this;
        setTimeout(function () {
          self.goToStep(nextStep);
        }, 250);
      }
    };

    PVQuizViking.prototype.updateMultiUI = function (step, selectedCount, maxSelections) {
      var counter = step.querySelector('[data-counter]');
      if (counter) {
        counter.textContent =
          maxSelections > 0 ? selectedCount + ' of ' + maxSelections + ' selected' : 'Select 1 or more';
      }

      var continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn) {
        continueBtn.hidden = selectedCount === 0;
        continueBtn.disabled = selectedCount === 0;
      }
    };

    PVQuizViking.prototype.restoreSelections = function (step) {
      var questionId = step.dataset.questionId;
      var answer = this.answers[questionId];
      if (!answer) return;

      var isMulti = step.dataset.selectionType === 'multi';
      var values = isMulti ? answer : [answer];

      step.querySelectorAll('.pv-quiz-viking__option').forEach(function (opt) {
        var isMatch = values.indexOf(opt.dataset.optionValue) !== -1;
        opt.classList.toggle('is-selected', isMatch);
        opt.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });

      if (isMulti) {
        var maxSelections = parseInt(step.dataset.maxSelections, 10) || 0;
        this.updateMultiUI(step, values.length, maxSelections);
      }
    };

    PVQuizViking.prototype.clearTimers = function () {
      this.timers.forEach(function (t) {
        clearTimeout(t);
      });
      this.timers = [];
    };

    PVQuizViking.prototype.populateAnswersSummary = function (step) {
      var self = this;
      step.querySelectorAll('[data-answer-summary]').forEach(function (el) {
        var value = self.answers[el.dataset.answerSummary];
        if (!value) return;
        el.textContent = Array.isArray(value)
          ? value.map(formatAnswerValue).join(', ')
          : formatAnswerValue(value);
      });
    };

    PVQuizViking.prototype.handleLoadingStep = function (step) {
      var self = this;

      var bar = step.querySelector('.pv-quiz-viking__progress-bar');
      if (bar) {
        requestAnimationFrame(function () {
          bar.style.width = '100%';
        });
      }

      step.querySelectorAll('.pv-quiz-viking__checklist-item').forEach(function (item) {
        var delay = parseInt(item.dataset.delay, 10) || 0;
        self.timers.push(
          setTimeout(function () {
            item.classList.add('is-complete');
          }, delay)
        );
      });

      var duration = parseInt(step.dataset.duration, 10) || 3000;
      self.timers.push(
        setTimeout(function () {
          self.goToStep(self.getNextStep(12));
        }, duration)
      );
    };

    PVQuizViking.prototype.toggleQ13Sections = function (visible) {
      document.querySelectorAll(Q13_SECTION_SELECTOR).forEach(function (el) {
        el.style.display = visible ? 'block' : 'none';
      });
    };

    PVQuizViking.prototype.populateOrderSummary = function (step) {
      var meta = this.meta;
      var purchaseType = meta.purchaseType || 'subscription';

      var badge = step.querySelector('[data-order-type-badge]');
      var quantityEl = step.querySelector('[data-order-quantity]');
      var productNameEl = step.querySelector('[data-order-product-name]');
      var originalEl = step.querySelector('[data-order-original-price]');
      var discountRow = step.querySelector('[data-order-discount-row]');
      var discountEl = step.querySelector('[data-order-discount]');
      var addonRow = step.querySelector('[data-order-addon-row]');
      var addonNameEl = step.querySelector('[data-order-addon-name]');
      var addonBadgeEl = step.querySelector('[data-order-addon-badge]');
      var totalEl = step.querySelector('[data-order-total-price]');

      if (badge) {
        badge.textContent = purchaseType === 'one_time' ? 'One-Time Purchase' : 'Subscription';
      }

      if (quantityEl) quantityEl.textContent = meta.pvTierLabel || '';
      if (productNameEl) productNameEl.textContent = meta.pvProductTitle || '';

      // The add-on is its own row in the same order card (quantity/name/badge,
      // same shape as the main product row) rather than a separate price line -
      // its cost is folded into the original/discount/total figures below.
      // Either accepted (added at checkout) or already sitting in the cart from
      // the boost card (kept at checkout) - it's charged once either way.
      var hasAddon = !!((meta.addonAccepted || meta.addonInCartVariantId) && meta.addonPrice);
      if (addonRow) addonRow.hidden = !hasAddon;
      if (hasAddon) {
        if (addonNameEl) addonNameEl.textContent = meta.addonTitle || 'Add-on';
        if (addonBadgeEl) addonBadgeEl.textContent = meta.addonSellingPlanId ? 'Subscription' : 'One-Time Purchase';
      }

      var addonValue = hasAddon ? parseMoneyValue(meta.addonPrice) : null;

      // "Total Today" always reflects what completeCheckout() actually charges
      // (see the addon push in that method) - fold the add-on's price in here
      // rather than quoting a total the cart will then exceed.
      var displayTotal = meta.pvPriceTotal || '';
      if (hasAddon) {
        var tierValue = parseMoneyValue(meta.pvPriceTotal);
        if (tierValue != null && addonValue != null) {
          displayTotal = formatMoneyLike(meta.pvPriceTotal, tierValue + addonValue);
        }
      }

      if (purchaseType === 'one_time') {
        if (originalEl) originalEl.textContent = displayTotal;
        if (totalEl) totalEl.textContent = displayTotal;
        if (discountRow) discountRow.hidden = true;
      } else {
        // Original price and discount fold in the add-on's own compare-at
        // price and savings too, so Original - Discount still equals Total
        // Today once the add-on's price no longer appears as its own line.
        var displayOriginal = meta.pvCompareTotal || '';
        var displayDiscount = meta.pvSaveText || '';

        if (hasAddon && meta.addonComparePrice) {
          var tierCompareValue = parseMoneyValue(meta.pvCompareTotal);
          var addonCompareValue = parseMoneyValue(meta.addonComparePrice);
          if (tierCompareValue != null && addonCompareValue != null) {
            displayOriginal = formatMoneyLike(meta.pvCompareTotal, tierCompareValue + addonCompareValue);

            var tierSaveValue = parseMoneyValue(meta.pvSaveText);
            var addonSaveValue = addonValue != null ? addonCompareValue - addonValue : 0;
            if (tierSaveValue != null) {
              displayDiscount = formatMoneyLike(meta.pvSaveText, tierSaveValue + addonSaveValue);
            }
          }
        }

        if (originalEl) originalEl.textContent = displayOriginal;
        if (totalEl) totalEl.textContent = displayTotal;
        if (discountEl) discountEl.textContent = displayDiscount;
        if (discountRow) discountRow.hidden = false;
      }

      var bulletsEl = step.querySelector('[data-order-bullets]');
      var P = window.PVQuizVikingPersonalization;
      if (bulletsEl && P) {
        var checkIcon = bulletsEl.dataset.checkIcon;
        bulletsEl.innerHTML = '';
        P.getBenefitBullets(this.answers.q1).forEach(function (text) {
          var li = document.createElement('li');
          if (checkIcon) {
            var icon = document.createElement('img');
            icon.src = checkIcon;
            icon.alt = '';
            li.appendChild(icon);
          }
          var span = document.createElement('span');
          span.textContent = text;
          li.appendChild(span);
          bulletsEl.appendChild(li);
        });
      }
    };

    // Re-resolved at checkout time (not frozen at accept time) so it always
    // reflects whichever tier is currently selected, even if the add-on was
    // accepted before the shopper settled on a jar count. Falls back to the
    // add-on's own default plan if none of its plans match the main
    // product's delivery frequency (e.g. the "1 Bottle" variant not yet
    // carrying the 48/96-day plans that the "2/4 Jars" tiers need).
    PVQuizViking.prototype.resolveAddonSellingPlanId = function () {
      var meta = this.meta;
      var targetDays = parseInt(meta.pvRefillDays, 10);
      var plans = meta.addonSellingPlans;

      if (Array.isArray(plans) && !isNaN(targetDays)) {
        var match = plans.filter(function (plan) {
          return parseDaysFromPlanName(plan.name) === targetDays;
        })[0];
        if (match) return match.id;
      }

      return meta.addonSellingPlanId;
    };

    PVQuizViking.prototype.completeCheckout = function () {
      var Cart = window.PVQuizVikingCart;
      if (!Cart) return;

      var items = Cart.buildCartItems(this.meta);

      // Single source of truth for the Libido Fire Drops add-on, whether it
      // was accepted early on the Offer Page's always-shown card or via the
      // conditional Screen 16 upsell - both just set this same meta.
      if (this.meta.addonAccepted && this.meta.addonVariantId) {
        var addonItem = { id: parseInt(this.meta.addonVariantId, 10), quantity: 1 };
        var addonSellingPlanId = this.resolveAddonSellingPlanId();
        if (addonSellingPlanId) {
          addonItem.selling_plan = parseInt(addonSellingPlanId, 10);
        }
        items.push(addonItem);
      }

      // Render step 17 ("Off To Checkout") synchronously instead of going
      // through goToStep(), since that relies on the async hashchange event -
      // we need the DOM updated immediately, before the /cart/add.js call.
      this.render(17);
      history.pushState(null, '', '#step-17');

      // The boost card's add-on is already a real cart line - checkout keeps
      // that line instead of clearing it, and never adds the variant twice.
      var keepVariantIds = this.meta.addonInCartVariantId ? [this.meta.addonInCartVariantId] : [];

      var currentStep = this.container.querySelector('.pv-quiz-viking__step.is-active');
      Cart.submitCartAndRedirect(items, this.buildCartAttributes(), keepVariantIds, function (err) {
        console.error('PV Quiz Viking: cart error', err);
        var errorEl = currentStep && currentStep.querySelector('[data-cart-error]');
        if (errorEl) {
          errorEl.textContent = 'Something went wrong adding this to your cart. Please try again.';
          errorEl.hidden = false;
        }
      });
    };

    PVQuizViking.prototype.populateCheckoutRecap = function (step) {
      var recapEl = step.querySelector('[data-checkout-recap]');
      if (!recapEl) return;

      var meta = this.meta;
      var purchaseType = meta.purchaseType || 'subscription';
      var label = purchaseType === 'one_time' ? 'One-Time Purchase' : 'Subscription';

      recapEl.textContent = label + ', ' + (meta.pvTierLabel || '') + '. Total ' + (meta.pvPriceTotal || '') + '.';
    };

    PVQuizViking.prototype.render = function (stepNumber) {
      this.clearTimers();
      this.toggleQ13Sections(stepNumber === 14);

      this.trackEvent('quiz_step_view', { step_number: stepNumber, step_name: STEP_NAMES[stepNumber] || null });

      var current = null;

      this.steps.forEach(function (step) {
        var isMatch = parseInt(step.dataset.step, 10) === stepNumber;
        step.classList.toggle('is-active', isMatch);
        if (isMatch) current = step;
      });

      this.container.querySelectorAll('[data-show-on-step]').forEach(function (el) {
        el.hidden = parseInt(el.dataset.showOnStep, 10) !== stepNumber;
      });

      if (!current) return;

      var stepType = current.dataset.stepType;

      if (stepType === 'question_single' || stepType === 'question_multi') {
        this.restoreSelections(current);
      } else if (stepType === 'results' || stepType === 'confirmation') {
        this.populateAnswersSummary(current);
      } else if (stepType === 'loading') {
        this.handleLoadingStep(current);
      } else if (stepType === 'email_gate') {
        this.handleEmailGateView(current);
      } else if (stepType === 'order_summary') {
        this.populateOrderSummary(current);
      } else if (stepType === 'checkout_redirect') {
        this.populateCheckoutRecap(current);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return PVQuizViking;
  })();

  function initQuizzes() {
    document.querySelectorAll('[data-section-type="pv-quiz-viking"]').forEach(function (container) {
      if (!container.pvQuizViking) {
        container.pvQuizViking = new PVQuizViking(container);
        // Single quiz instance per page - the Offer Page sections live outside
        // this container in the DOM and reach the instance through this global.
        window.PVQuizVikingApp = container.pvQuizViking;
      }
    });
  }

  // Sticky buy bar on the Offer Page (step 14): mirrors the real buy button's
  // price/compare text and shows once the shopper scrolls past it. The bar
  // itself lives inside the __q13_product section, so it is already hidden
  // whenever that section is (i.e. on every step except 14) - this only
  // handles the scroll-triggered reveal and keeping its text in sync.
  function initStickyBuyBar() {
    var bar = document.querySelector('[data-pv-sticky-bar]');
    var realBtn = document.querySelector('.pv-product__buy .pv-product__buy-btn');
    if (!bar || !realBtn) return;

    var stickyBtn = bar.querySelector('[data-pv-sticky-btn]');
    var realPrice = document.querySelector('.pv-product__buy [data-pv-btn-price]');
    var realCompare = document.querySelector('.pv-product__buy [data-pv-btn-compare]');
    var stickyPrice = bar.querySelector('[data-pv-sticky-price]');
    var stickyCompare = bar.querySelector('[data-pv-sticky-compare]');

    function sync() {
      if (stickyPrice && realPrice) stickyPrice.textContent = realPrice.textContent;
      if (stickyCompare && realCompare) stickyCompare.textContent = realCompare.textContent;
      if (stickyBtn) stickyBtn.disabled = realBtn.disabled;
    }

    sync();

    if (window.MutationObserver) {
      var observer = new MutationObserver(sync);
      [realPrice, realCompare].forEach(function (el) {
        if (el) observer.observe(el, { childList: true, characterData: true, subtree: true });
      });
      observer.observe(realBtn, { attributes: true, attributeFilter: ['disabled'] });
    }

    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      io.observe(realBtn);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initQuizzes();
      initStickyBuyBar();
    });
  } else {
    initQuizzes();
    initStickyBuyBar();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var container = e.target.querySelector('[data-section-type="pv-quiz-viking"]');
    if (container) {
      container.pvQuizViking = new PVQuizViking(container);
    }
  });
})();
